import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import newsService from '../../services/newsService';
import championshipService from '../../services/championshipService';
import { FaFeatherAlt, FaCloudUploadAlt, FaHistory, FaEdit, FaTrashAlt } from 'react-icons/fa';
import './ChampionshipNews.css';

// Modal de Edição
const EditArticleModal = ({ isOpen, onClose, article, onSave, isSaving }) => {
  const [title, setTitle] = useState(article?.title || '');
  const [content, setContent] = useState(article?.content || '');

  useEffect(() => {
    if (article) {
      setTitle(article.title);
      setContent(article.content);
    }
  }, [article]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h3>Editar Notícia</h3>
        <div className="form-group">
          <label>Título:</label>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Conteúdo:</label>
          <textarea value={content} onChange={e => setContent(e.target.value)} rows="10"></textarea>
        </div>
        <div className="modal-actions">
          <button onClick={onClose} disabled={isSaving}>Cancelar</button>
          <button onClick={() => onSave(article._id, title, content)} disabled={isSaving}>
            {isSaving ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
};


function ChampionshipNews({ championshipId: propChampionshipId }) {
  const { id: urlId } = useParams();
  const championshipId = propChampionshipId || urlId;

  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  const [selectedType, setSelectedType] = useState('round'); 
  const [selectedValue, setSelectedValue] = useState('1');   
  const [availableRounds, setAvailableRounds] = useState([]); 
  const [availablePhases, setAvailablePhases] = useState([]); 

  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });
  const [championship, setChampionship] = useState(null);
  const [loadingChampionship, setLoadingChampionship] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [articleToEdit, setArticleToEdit] = useState(null);
  const [isSavingArticle, setIsSavingArticle] = useState(false);


  const phaseDisplayNames = useMemo(() => ({
    'group': 'Fase de Grupos', 
    'roundOf16': 'Oitavas de Final',
    'quarterFinals': 'Quartas de Final',
    'semiFinals': 'Semifinais',
    'thirdPlace': 'Disputa de 3º Lugar',
    'final': 'Final'
  }), []);

  const isChampionshipManager = useCallback(() => {
    if (!user || !championship) return false;
    
    const userId = user._id;
    const isCreator = championship.creator?._id === userId;
    const isManager = championship.managers?.some(m => m._id === userId);
    
    return isCreator || isManager;
  }, [user, championship]);

  const canManage = useMemo(() => {
    return user?.role && ['Admin', 'Gestor'].includes(user.role) && isChampionshipManager();
  }, [user, isChampionshipManager]);


  useEffect(() => {
    const fetchChampionshipDetails = async () => {
      if (!championshipId) return;
      setLoadingChampionship(true);
      try {
        const champRes = await championshipService.getById(championshipId);
        const details = champRes.data || champRes;
        setChampionship(details);

        const totalTeamsInGroups = details.groups?.reduce((acc, g) => acc + (g.teams?.length || 0), 0) || 0;
        const maxRounds = totalTeamsInGroups > 0 ? Math.ceil(totalTeamsInGroups / 2) * (totalTeamsInGroups % 2 === 0 ? 2 : 1) - 1 : 1;
        
        setAvailableRounds(Array.from({ length: Math.max(1, maxRounds) }, (_, i) => String(i + 1)));

        // ✅✅✅ LÓGICA CORRIGIDA E COMPLETA ✅✅✅
        const knockoutPhasesForFormat = [];
        const format = details.format;

        if (format === 'Fase de grupos + Mata-mata (32 times)' || format === 'Mata-mata (16 times)') {
            // Estes formatos começam nas Oitavas de Final.
            knockoutPhasesForFormat.push('roundOf16', 'quarterFinals', 'semiFinals', 'final', 'thirdPlace');
        } else if (format === 'Fase de grupos + Mata-mata (16 times)' || format === 'Mata-mata (8 times)') {
            // Estes formatos começam nas Quartas de Final.
            knockoutPhasesForFormat.push('quarterFinals', 'semiFinals', 'final', 'thirdPlace');
        } else if (format === 'Mata-mata (4 times)') {
            // Este formato começa nas Semifinais.
            knockoutPhasesForFormat.push('semiFinals', 'final', 'thirdPlace');
        } else if (format === 'Formato Personalizado') {
            // Para formatos personalizados, é mais seguro oferecer todas as opções.
            knockoutPhasesForFormat.push('roundOf16', 'quarterFinals', 'semiFinals', 'final', 'thirdPlace');
        }
        setAvailablePhases(knockoutPhasesForFormat);
        
        // Define o valor inicial dos seletores com base nos dados carregados
        if (selectedType === 'round' && availableRounds.length > 0) {
            setSelectedValue(availableRounds[0]);
        } else if (selectedType === 'phase' && knockoutPhasesForFormat.length > 0) {
            setSelectedValue(knockoutPhasesForFormat[0]);
        }

      } catch (error) {
        console.error('Erro ao carregar dados do campeonato:', error);
      } finally {
        setLoadingChampionship(false);
      }
    };

    fetchChampionshipDetails();
  }, [championshipId]);

  const fetchArticles = useCallback(async () => {
    if (!championshipId) return;
    setLoading(true);
    try {
      const fetchedArticles = canManage
        ? await newsService.listAllForAdmin(championshipId)
        : await newsService.listPublished(championshipId);
      
      setArticles(
        fetchedArticles.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      );
      setStatusMessage({ text: '', type: '' });
    } catch (error) {
      setStatusMessage({ text: '❌ Falha ao carregar as notícias.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [championshipId, canManage]);

  useEffect(() => { 
    if (championship) {
      fetchArticles(); 
    }
  }, [fetchArticles, championship]);

  const handleGenerateClick = async () => {
    if (!canManage) return;
    if (!selectedValue) {
        setStatusMessage({ text: 'Por favor, selecione uma rodada ou fase para gerar a notícia.', type: 'error' });
        return;
    }
    
    setGenerating(true);
    const identifier = selectedType === 'round' ? `rodada ${selectedValue}` : phaseDisplayNames[selectedValue];
    setStatusMessage({ text: `Gerando notícia para a ${identifier}...`, type: 'loading' });
    
    try {
      const response = await newsService.generate(championshipId, selectedType, selectedValue); 
      setStatusMessage({ text: `✅ ${response.message}`, type: 'success' });
      fetchArticles();
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Falha ao gerar notícia.';
      setStatusMessage({ text: `❌ Erro: ${errorMsg}`, type: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handlePublishClick = async (articleId) => {
    if (!canManage) return;
    
    const targetArticle = articles.find(a => a._id === articleId);
    if (targetArticle?.isPublishing) return;

    setArticles(current =>
      current.map(a => a._id === articleId ? { ...a, isPublishing: true } : a)
    );

    try {
      await newsService.publish(articleId);
      setStatusMessage({ text: '✅ Notícia publicada com sucesso!', type: 'success' });
      await fetchArticles();
    } catch (error) {
      setStatusMessage({ text: '❌ Erro ao publicar a notícia.', type: 'error' });
      setArticles(current =>
        current.map(a => a._id === articleId ? { ...a, isPublishing: false } : a)
      );
    }
  };

  const handleEditClick = (article) => {
    if (!canManage) {
      setStatusMessage({ text: 'Você não tem permissão para editar notícias.', type: 'error' });
      return;
    }
    setArticleToEdit(article);
    setIsEditModalOpen(true);
  };

  const handleSaveEditedArticle = async (articleId, newTitle, newContent) => {
    setIsSavingArticle(true);
    try {
      await newsService.update(articleId, { title: newTitle, content: newContent });
      setStatusMessage({ text: '✅ Notícia atualizada com sucesso!', type: 'success' });
      await fetchArticles();
      setIsEditModalOpen(false);
    } catch (error) {
      setStatusMessage({ text: '❌ Erro ao atualizar notícia.', type: 'error' });
    } finally {
      setIsSavingArticle(false);
    }
  };

  const handleDeleteClick = async (articleId, articleTitle) => {
    if (!canManage) {
      setStatusMessage({ text: 'Você não tem permissão para excluir notícias.', type: 'error' });
      return;
    }
    if (window.confirm(`Tem certeza que deseja excluir a notícia "${articleTitle}"?`)) {
      try {
        await newsService.delete(articleId);
        setStatusMessage({ text: '✅ Notícia excluída com sucesso!', type: 'success' });
        await fetchArticles();
      } catch (error) {
        setStatusMessage({ text: '❌ Erro ao excluir notícia.', type: 'error' });
      }
    }
  };

  if (loadingChampionship) {
    return <div className="loading-message">Carregando informações do campeonato...</div>;
  }

  return (
    <div className="news-container">
      {canManage && (
        <div className="generate-news-panel">
          <h4><FaFeatherAlt /> Gerador de Notícias</h4>
          <div className="controls">
            <label htmlFor="typeSelector">Identificador:</label>
            <select id="typeSelector" value={selectedType} onChange={(e) => {setSelectedType(e.target.value); setSelectedValue(e.target.value === 'round' ? availableRounds[0] || '1' : availablePhases[0] || '');}} disabled={generating}>
              <option value="round">Rodada</option>
              {availablePhases.length > 0 && <option value="phase">Fase de Mata-Mata</option>}
            </select>

            {selectedType === 'round' && (
              <input
                type="number"
                value={selectedValue}
                onChange={(e) => setSelectedValue(e.target.value)}
                placeholder="Nº da Rodada"
                min="1"
                max={availableRounds.length > 0 ? availableRounds[availableRounds.length - 1] : "1"}
                disabled={generating || availableRounds.length === 0}
              />
            )}

            {selectedType === 'phase' && (
              <select value={selectedValue} onChange={(e) => setSelectedValue(e.target.value)} disabled={generating || availablePhases.length === 0}>
                {availablePhases.length === 0 && <option value="">Nenhuma fase disponível</option>}
                {availablePhases.map(phase => (
                  <option key={phase} value={phase}>{phaseDisplayNames[phase] || phase}</option>
                ))}
              </select>
            )}

            <button 
              onClick={handleGenerateClick} 
              disabled={generating || !selectedValue || !canManage}
              title={!canManage ? "Você não tem permissão para gerar notícias" : ""}
            >
              {generating ? 'Gerando...' : 'Gerar com IA'}
            </button>
          </div>
          {statusMessage.text && (
            <p className={`status-message ${statusMessage.type}`}>
              {statusMessage.text}
            </p>
          )}
        </div>
      )}

      <div className="feed-header">
        <FaHistory />
        <h2>Últimas Notícias</h2>
      </div>

      {loading ? (
        <p className="loading-message">Carregando notícias...</p>
      ) : (
        <div className="articles-list">
          {articles.length === 0 ? (
            <div className="no-news-message">
              <p>Nenhuma notícia encontrada para este campeonato.</p>
            </div>
          ) : (
            articles.map((article, index) => (
              <div
                key={article._id}
                className={`article-card ${article.status}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {canManage && article.status === 'draft' && (
                  <div className="status-badge draft-badge">Rascunho</div>
                )}
                
                <div className="article-header">
                  <h3>{article.title}</h3>
                  <p className="article-meta">
                    {article.phase && article.phase !== 'group' && <span>Fase: {phaseDisplayNames[article.phase] || article.phase}</span>}
                    {article.roundNumber > 0 && <span>Rodada: {article.roundNumber}</span>}
                    <span>
                      {article.status === 'published'
                        ? `Publicado em ${new Date(article.publishedAt).toLocaleDateString('pt-BR')}`
                        : `Criado em ${new Date(article.createdAt).toLocaleDateString('pt-BR')}`
                      }
                    </span>
                  </p>
                </div>

                <div
                  className="article-content"
                  dangerouslySetInnerHTML={{ __html: article.content?.replace(/\n/g, '<br />') || '' }}
                />

                {canManage && (
                  <div className="admin-actions">
                    {article.status === 'draft' && (
                      <button
                        onClick={() => handlePublishClick(article._id)}
                        className="btn-publish"
                        disabled={article.isPublishing}
                        title={!canManage ? "Você não tem permissão para publicar notícias" : ""}
                      >
                        <FaCloudUploadAlt />
                        {article.isPublishing ? 'Publicando...' : 'Aprovar e Publicar'}
                      </button>
                    )}
                    <button
                      onClick={() => handleEditClick(article)}
                      className="btn-edit"
                      title={!canManage ? "Você não tem permissão para editar notícias" : ""}
                    >
                      <FaEdit /> Editar
                    </button>
                    <button
                      onClick={() => handleDeleteClick(article._id, article.title)}
                      className="btn-delete"
                      title={!canManage ? "Você não tem permissão para excluir notícias" : ""}
                    >
                      <FaTrashAlt /> Excluir
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      <EditArticleModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        article={articleToEdit}
        onSave={handleSaveEditedArticle}
        isSaving={isSavingArticle}
      />
    </div>
  );
}

export default ChampionshipNews;